import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useTheme } from "next-themes";
import {
  LayoutDashboard, Users, ListChecks, Calendar, BarChart3, MessageCircle,
  UserCog, Zap, Settings as SettingsIcon, Moon, Sun, LogOut, Menu, X,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import GlobalSearch from "@/components/crm/GlobalSearch";
import NotificationBell from "@/components/crm/NotificationBell";
import { UserAvatar } from "@/components/crm/Badges";
import { LogoMark } from "@/components/crm/Logo";
import AppointmentReminders from "@/components/crm/AppointmentReminders";
import ScrollRestorer from "@/components/crm/ScrollRestorer";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/leads", label: "Lead", icon: Users },
  { to: "/attivita", label: "Attività", icon: ListChecks },
  { to: "/calendario", label: "Calendario", icon: Calendar },
  { to: "/whatsapp", label: "WhatsApp", icon: MessageCircle },
  { to: "/report", label: "Report", icon: BarChart3 },
];
const ADMIN_NAV = [
  { to: "/team", label: "Team", icon: UserCog },
  { to: "/automazioni", label: "Automazioni", icon: Zap },
  { to: "/impostazioni", label: "Impostazioni", icon: SettingsIcon },
];

export default function Layout() {
  const { user, logout, isAdmin } = useAuth();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const nav = isAdmin ? [...NAV, ...ADMIN_NAV] : NAV;

  const NavItems = () => (
    <nav className="flex flex-col gap-0.5 px-2">
      {nav.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          data-testid={`nav-${item.label.toLowerCase()}`}
          onClick={() => setMobileOpen(false)}
          className={({ isActive }) =>
            cn(
              "flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors",
              isActive
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:text-foreground hover:bg-accent"
            )
          }
        >
          <item.icon className="w-4 h-4" />
          {item.label}
        </NavLink>
      ))}
    </nav>
  );

  const Brand = () => (
    <div className="flex items-center gap-2.5 px-4 h-14 border-b shrink-0">
      <LogoMark size={32} />
      <div className="leading-none">
        <div className="font-heading font-extrabold tracking-tight">alternativeADV</div>
        <div className="text-[10px] text-muted-foreground overline mt-0.5">CRM FORMAZIONE ABILITANTE</div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex bg-background">
      <AppointmentReminders />
      {/* Desktop sidebar */}
      <aside className="hidden md:flex flex-col w-60 border-r bg-card fixed inset-y-0 left-0 z-30">
        <Brand />
        <div className="flex-1 overflow-y-auto py-3">
          <div className="overline text-muted-foreground px-5 mb-2">Menu</div>
          <NavItems />
        </div>
        <div className="p-2 border-t">
          <div className="flex items-center gap-2 px-2 py-2">
            <UserAvatar user={user} size={32} />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{user?.name}</div>
              <div className="text-xs text-muted-foreground capitalize">{user?.role}</div>
            </div>
            <button data-testid="account-link" onClick={() => navigate("/account")}
              className="p-2 rounded-md hover:bg-accent transition-colors" title="Impostazioni personali">
              <SettingsIcon className="w-4 h-4" />
            </button>
            <button data-testid="logout-btn" onClick={async () => { await logout(); navigate("/login"); }}
              className="p-2 rounded-md hover:bg-accent transition-colors" title="Esci">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-40 flex">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <aside className="relative w-64 bg-card flex flex-col animate-fade-up">
            <Brand />
            <div className="flex-1 overflow-y-auto py-3"><NavItems /></div>
            <div className="p-2 border-t">
              <button onClick={async () => { await logout(); navigate("/login"); }}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-md hover:bg-accent text-sm">
                <LogOut className="w-4 h-4" /> Esci
              </button>
            </div>
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 md:ml-60 flex flex-col min-w-0">
        <header className="sticky top-0 z-20 h-14 border-b bg-background/80 backdrop-blur-lg flex items-center gap-3 px-4">
          <button className="md:hidden p-2 -ml-2" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn">
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
          <GlobalSearch />
          <div className="ml-auto flex items-center gap-1">
            <NotificationBell />
            <button
              data-testid="theme-toggle"
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
              className="p-2 rounded-md hover:bg-accent transition-colors"
            >
              {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
          </div>
        </header>
        <main className="flex-1 p-4 md:p-6 lg:p-8 max-w-[1500px] w-full mx-auto">
          <ScrollRestorer />
          <Outlet />
        </main>
      </div>
    </div>
  );
}
